import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ClipboardCheck,
  Target,
  Zap,
  TrendingUp,
} from "lucide-react";

const WerkwijzePage = () => {
  const steps = [
    {
      number: "01",
      icon: ClipboardCheck,
      title: "Gratis Intake & Analyse",
      description:
        "We beginnen met een volledig gratis en vrijblijvend intake gesprek. We bespreken je doelen, je huidige situatie, eventuele blessures of beperkingen, en je beschikbare tijd. Daarnaast doen we een fysieke analyse om je huidige conditie in kaart te brengen.",
      details: [
        "Volledig gratis en vrijblijvend",
        "Persoonlijk gesprek over doelen",
        "Fysieke assessment",
        "Analyse van leefstijl en voeding",
      ],
    },
    {
      number: "02",
      icon: Target,
      title: "Persoonlijk Plan",
      description:
        "Op basis van de intake stellen we een volledig op maat gemaakt trainingsplan op. Dit plan is afgestemd op jouw specifieke doelen, niveau en mogelijkheden. We bepalen ook de frequentie en het type training dat het beste bij je past.",
      details: [
        "Maatwerk trainingsschema",
        "Realistische doelen",
        "Voedingsadvies op maat",
        "Planning van sessies",
      ],
    },
    {
      number: "03",
      icon: Zap,
      title: "Coaching & Begeleiding",
      description:
        "Nu begint het echte werk. Onder professionele begeleiding van onze coaches werk je aan je doelen. Elke sessie wordt begeleid en we zorgen ervoor dat je de oefeningen correct uitvoert voor maximaal resultaat en minimale blessurerisico.",
      details: [
        "Professionele begeleiding",
        "Correcte techniek",
        "Motivatie en support",
        "Variatie in training",
      ],
    },
    {
      number: "04",
      icon: TrendingUp,
      title: "Meten & Optimaliseren",
      description:
        "Regelmatig evalueren we je voortgang. We meten resultaten, bespreken wat goed gaat en wat beter kan, en passen waar nodig het programma aan. Zo blijf je continue groeien en behaal je optimale resultaten.",
      details: [
        "Regelmatige voortgangsmetingen",
        "Evaluatiegesprekken",
        "Programma optimalisatie",
        "Viering van successen",
      ],
    },
  ];

  return (
    <main data-testid="werkwijze-page" className="pt-20">
      {/* Hero Section */}
      <section className="section bg-[#0A0A0A]" data-testid="werkwijze-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Onze Werkwijze</span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Hoe Wij
              <br />
              <span className="text-red-500">Te Werk Gaan</span>
            </h1>
            <p className="text-zinc-400 text-lg">
              Een gestructureerde aanpak voor gegarandeerd resultaat. Van intake
              tot transformatie - wij begeleiden je bij elke stap.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section bg-[#121212]" data-testid="timeline-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="timeline">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="timeline-item"
                  data-testid={`timeline-step-${index}`}
                >
                  <div className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-sm ml-8 hover:border-red-500/30 transition-all duration-500">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
                          <step.icon className="w-8 h-8 text-red-500" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-2">
                          <span className="text-red-500 font-mono text-sm">
                            {step.number}
                          </span>
                          <h3 className="text-2xl md:text-3xl font-black text-white font-['Barlow_Condensed'] uppercase">
                            {step.title}
                          </h3>
                        </div>
                        <p className="text-zinc-400 leading-relaxed mb-6">
                          {step.description}
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {step.details.map((detail, i) => (
                            <li
                              key={i}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                              <span className="text-zinc-300">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why This Works */}
      <section className="section bg-[#0A0A0A]" data-testid="why-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="caption mb-4 block">Waarom Dit Werkt</span>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
                Structuur Leidt Tot{" "}
                <span className="text-red-500">Resultaat</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Onze gestructureerde aanpak is gebaseerd op jaren ervaring en
                bewezen methodieken. Door te werken met duidelijke fases en
                regelmatige evaluaties, zorgen we ervoor dat je continue
                vooruitgang boekt.
              </p>
              <p className="text-zinc-400 leading-relaxed mb-8">
                Geen giswerk, geen vage beloftes. Met Arena Gym weet je precies waar
                je aan toe bent en wat je kunt verwachten. En het belangrijkste:
                je behaalt de resultaten die je wilt.
              </p>

              <div className="grid grid-cols-2 gap-6">
                {[
                  { number: "98%", label: "Behaalt doelen" },
                  { number: "4 weken", label: "Tot eerste resultaat" },
                ].map((stat, index) => (
                  <div key={stat.label}>
                    <div className="text-4xl font-black text-red-500 font-['Barlow_Condensed']">
                      {stat.number}
                    </div>
                    <div className="text-zinc-500 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-square rounded-sm overflow-hidden border border-zinc-800">
                <img
                  src="/coaching-jan.jpeg"
                  alt="Training Session"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-[#121212]" data-testid="werkwijze-cta">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Begin Vandaag</span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Klaar Voor <span className="text-red-500">Stap 1</span>?
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              De eerste stap naar jouw transformatie begint met een volledig gratis intake. Boek
              nu je afspraak en ontdek wat Arena Gym voor jou kan betekenen.
            </p>
            <Link
              to="/boeken"
              data-testid="werkwijze-book-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              Boek Je Intake
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default WerkwijzePage;
